import * as React from "react";
import { useCallback } from "react";
import { Download } from "lucide-react";
import type { Exporter } from "ra-core";
import {
  fetchRelatedRecords,
  useCanAccess,
  useDataProvider,
  useGetResourceLabel,
  useListContext,
  useNotify,
  useResourceTranslation,
} from "ra-core";
import { Button } from "@/components/ui/button";

export const ExportButton = (props: ExportButtonProps) => {
  const {
    maxResults = 1000,
    onClick,
    label: labelProp,
    icon = defaultIcon,
    exporter: customExporter,
    meta,
    className = "cursor-pointer",
  } = props;
  const {
    getData,
    total,
    resource,
    exporter: exporterFromContext,
  } = useListContext();
  const access = useCanAccess({ action: "export", resource });
  const getResourceLabel = useGetResourceLabel();
  const label = useResourceTranslation({
    resourceI18nKey: `resources.${resource}.action.export`,
    baseI18nKey: "ra.action.export",
    options: { name: getResourceLabel(resource, 1) },
    userText: labelProp,
  });
  const exporter = customExporter || exporterFromContext;
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const handleClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!getData) {
        throw new Error(
          "ListContext.getData must be defined to use ExportButton.",
        );
      }
      try {
        const data = await getData({ maxResults, meta });
        const auditProvider = dataProvider as typeof dataProvider & {
          recordExport?: (resource: string, count: number) => Promise<void>;
        };
        await auditProvider.recordExport?.(resource, data.length);
        if (typeof exporter === "function") {
          await exporter(
            data,
            fetchRelatedRecords(dataProvider),
            dataProvider,
            resource,
          );
        }
      } catch (error) {
        console.error(error);
        notify("No se pudo exportar la informacion", { type: "error" });
      }
      onClick?.(event);
    },
    [
      dataProvider,
      exporter,
      getData,
      maxResults,
      meta,
      notify,
      onClick,
      resource,
    ],
  );

  if (access.isPending || !access.canAccess) return null;

  return (
    <Button
      variant="outline"
      onClick={handleClick}
      disabled={total === 0}
      className={className}
    >
      {icon}
      {label}
    </Button>
  );
};

const defaultIcon = <Download />;

export interface ExportButtonProps {
  className?: string;
  exporter?: Exporter;
  icon?: React.ReactNode;
  label?: string;
  maxResults?: number;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  resource?: string;
  meta?: Record<string, unknown>;
}
